import {
  Button,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { faArrowDownToArc, faArrowUpFromArc } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { organizationListContext } from 'brokertslib/src/context'
import type {
  OrganizationUnitGridItem,
  OrganizationUnitPagedQuery
} from 'BrokerTsLib/src/dtos'
import {
  useGetAll,
  useTransferOrganizationUnit as useTransferOrganization
} from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useState } from 'react'

import { HeaderButton } from '@/components'

import { Pagination } from '../Pagination'
import { OrganizationListCityColumn, OrganizationListDescriptionColumn, OrganizationListNameColumn, OrganizationListTypeColumn, OrganizationsList } from './OrganizationList'
import { OrganizationListFilter } from './OrganizationListFilter'


function getAllDependentOrganizationUnits(items: { id: number, superiorOrganizationUnitId?: number }[], superiorOrganizationUnitId?: number) {
  // Find all direct children of the given parentId
  const children = items.filter(item => item.superiorOrganizationUnitId === superiorOrganizationUnitId)

  // Collect their IDs
  let result = children.map(child => child.id)

  // For each child, recursively get its children
  for (const child of children) {
    result = result.concat(getAllDependentOrganizationUnits(items, child.id))
  }

  return result
}

const pageSize = 15
export const OrganizationTransferButton = ({
  id,
  superiorOrganizationUnitId,
}: { id?: number, superiorOrganizationUnitId?: number }) => {
  const toast = useToast()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const [filter, setFilter] = useState<OrganizationUnitPagedQuery>({
    showInvalid: false,
    showRootOnly: false,
    pageIndex: 1,
    pageSize,
    showAllItems: true,
  })

  const { data, refetch, isLoading } = useGetAll(filter, { enabled: isOpen })
  const { mutateAsync: transferOrganization, isPending: isTransferOrganizationLoading } =
        useTransferOrganization()

  const dependentOrganizationIds = getAllDependentOrganizationUnits(data?.data.items ?? [], id)

  const possibleOrganizations =
        data?.data.items.filter((x) => x.id !== id && x.id !== superiorOrganizationUnitId && !dependentOrganizationIds.includes(x.id)) ?? []

  const handleTransfer = (organization: OrganizationUnitGridItem) => {
    transferOrganization({ organizationUnitId: id!, targetOrganizationUnitId: organization.id })
      .then(() => {
        toast({
          title: `Organizace byla přesunuta pod organizaci ${organization.name}.`,
          status: 'success',
        })
      })
      .catch(() => {
        toast({
          title: `Chyba při přesunu organizace pod organizaci ${organization.name}.`,
          status: 'error',
        })
      })
    onClose()
  }

  const handleChangeToRoot = () => {
    transferOrganization({ organizationUnitId: id!, targetOrganizationUnitId: undefined })
      .then(() => {
        toast({
          title: 'Organizace byla převedena na root organizaci.',
          status: 'success',
        })
      })
      .catch(() => {
        toast({
          title: 'Chyba při převodu na root organizaci.',
          status: 'error',
        })
      })
    onClose()
  }

  return (
    <>
      <HeaderButton onClick={onOpen} isLoading={isTransferOrganizationLoading} title="Přesunout pod jinou organizaci">
        <Icon as={FontAwesomeIcon} icon={faArrowUpFromArc} size="lg" color="secondary.500" />
        Přesunout
      </HeaderButton>
      <Modal size="6xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text>Přesunout organizaci</Text>
            </HStack>
            <ModalCloseButton />
            <HStack justifyContent='space-between'>
              <Text fontWeight='normal' fontSize='sm' my={2} maxW='2xl'>
                Přesunutí organizace pod jinou organizaci způsobí, že se všechny podřízené organizace této organizace také přesunou pod novou nadřazenou organizaci.
                Organizaci nelze přesunout pod již podřízenou organizaci k organizaci přesouvané.
                Pokud si přejete, aby organizace byla samostatná tzv. root organizace, tak použijte tlačítko vpravo.
              </Text>
              <Button isLoading={isTransferOrganizationLoading} onClick={handleChangeToRoot} isDisabled={superiorOrganizationUnitId === undefined}>
                <Icon as={FontAwesomeIcon} icon={faArrowUpFromArc} size="lg" color="secondary.500" mr={2} />
                Převést na root organizaci
              </Button>
            </HStack>
          </ModalHeader>

          <ModalBody sx={{ pt: 0, pb: 6, gap: 4 }}>
            <organizationListContext.Provider
              value={{
                loading: isLoading,
                totalCount: data?.data.totalCount ?? 0,
                currentPage: filter.pageIndex,
                pageSize,
                filter,
                setCurrentPage: () => { },
                setPageSize: () => { },
                updateFilter: setFilter,
                reload: () => refetch(),
              }}
            >
              <Flex flexDir={'column'} gap={4}>
                <OrganizationListFilter />
                <OrganizationsList
                  organizations={possibleOrganizations}
                  sx={{ boxShadow: 'none' }}
                  columnsDefinition={[
                    OrganizationListNameColumn,
                    OrganizationListDescriptionColumn,
                    OrganizationListCityColumn,
                    OrganizationListTypeColumn,
                    {
                      key: 'actions',
                      name: '',
                      cellExpr: (item) => (
                        <Button
                          size={'sm'}
                          colorScheme={'primary'}
                          onClick={() => handleTransfer(item)}
                        >
                          <HStack>
                            <Icon as={FontAwesomeIcon} icon={faArrowDownToArc} />
                            <Text color='white'>
                              Přesunout
                            </Text>
                          </HStack>
                        </Button>
                      ),
                    },
                  ]}
                />
                <Pagination context={organizationListContext} />
              </Flex>
            </organizationListContext.Provider>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
